import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom'; // FIX: Imported createPortal
import {
    X, Save, AlertCircle, Tag, Terminal, Loader2,
    Globe, User, Monitor, MapPin, Database, Calendar
} from 'lucide-react';
import { useUpdateIncidentMutation } from '@/lib/services/incidentApi';
import {
    ThreatIntelRecordDetail,
    ThreatIntelRecordUpdate,
    SEVERITY_OPTIONS,
    STATUS_OPTIONS,
    SeverityType,
    StatusType
} from '@/lib/api/types';
import { Input } from '@/components/common/ui/Input';
import { Textarea } from '@/components/common/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/ui/Select';

interface EditIncidentModalProps {
    incident: ThreatIntelRecordDetail;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

const EditIncidentModal: React.FC<EditIncidentModalProps> = ({
    incident,
    isOpen,
    onClose,
    onSave
}) => {
    const [updateIncident, { isLoading, error }] = useUpdateIncidentMutation();

    // Form state
    const [formData, setFormData] = useState<ThreatIntelRecordUpdate>({
        severity: incident.severity,
        status: incident.status,
        notes: incident.notes || '',
        source: incident.source || '',
        tags: incident.tags || {}
    });

    const [newTag, setNewTag] = useState('');
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Reset form when incident changes
    useEffect(() => {
        setFormData({
            severity: incident.severity,
            status: incident.status,
            notes: incident.notes || '',
            source: incident.source || '',
            tags: incident.tags || {}
        });
        setValidationErrors({});
        setNewTag('');
    }, [incident]);

    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Lock body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    const handleInputChange = (field: keyof ThreatIntelRecordUpdate) => (
        value: string | SeverityType | StatusType
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleAddTag = () => {
        if (!newTag.trim()) return;

        const tagKey = newTag.trim().toLowerCase().replace(/\s+/g, '_');
        setFormData(prev => ({
            ...prev,
            tags: {
                ...prev.tags,
                [tagKey]: newTag.trim()
            }
        }));
        setNewTag('');
    };

    const handleRemoveTag = (tagKey: string) => {
        setFormData(prev => {
            const { [tagKey]: _, ...restTags } = prev.tags || {};
            return {
                ...prev,
                tags: restTags
            };
        });
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.severity) {
            errors.severity = 'Severity is required';
        }

        if (!formData.status) {
            errors.status = 'Status is required';
        }

        if (formData.source && formData.source.length > 255) {
            errors.source = 'Source must be less than 255 characters';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await updateIncident({
                id: incident.id,
                data: formData
            }).unwrap();

            onSave();
        } catch (err) {
            console.error('Failed to update incident:', err);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddTag();
        }
    };

    if (!isOpen) return null;

    // FIX: Wrapped entire return block in createPortal so it escapes all z-index traps
    const modalContent = (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            {/* Backdrop click handler */}
            <div className="absolute inset-0" onClick={onClose} />

            {/* Modal - FIX: Added max-h-[90vh] to restrict infinite vertical expansion */}
            <div className="relative w-full max-w-2xl max-h-[90vh] bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden animate-fade-in font-mono">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-[#1A1D24] bg-[#12151C] flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h2 className="text-[14px] font-bold text-white tracking-widest uppercase flex items-center gap-2">
                            <Terminal className="w-5 h-5 text-[#00FFFF]" />
                            Modify Node Parameters
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-500 hover:text-[#FF3333] hover:bg-[#161A23] rounded-lg transition-all ml-2 border border-transparent hover:border-[#1A1D24]"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Form - FIX: Added min-h-0 so the custom-scrollbar is actually triggered */}
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar cyber-form-override">
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">

                        {/* Incident Details - Read Only */}
                        <div className="bg-[#050505] border border-[#1A1D24] rounded-lg p-5 space-y-4">
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest border-b border-[#1A1D24] pb-2">
                                Locked Core Identifiers
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
                                {incident.url ? (
                                    <div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 uppercase tracking-widest mb-1">
                                            <Globe className="h-3 w-3" /> URL Target
                                        </div>
                                        <div className="text-[12px] text-white break-all">{incident.url}</div>
                                    </div>
                                ) : null}
                                {incident.username ? (
                                    <div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 uppercase tracking-widest mb-1">
                                            <User className="h-3 w-3" /> Username
                                        </div>
                                        <div className="text-[12px] text-white">{incident.username}</div>
                                    </div>
                                ) : null}
                                {incident.ip ? (
                                    <div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 uppercase tracking-widest mb-1">
                                            <MapPin className="h-3 w-3" /> IP Address
                                        </div>
                                        <div className="text-[12px] text-[#00FFFF]">{incident.ip}</div>
                                    </div>
                                ) : null}
                                {incident.hostname ? (
                                    <div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 uppercase tracking-widest mb-1">
                                            <Monitor className="h-3 w-3" /> Hostname
                                        </div>
                                        <div className="text-[12px] text-white">{incident.hostname}</div>
                                    </div>
                                ) : null}
                                {incident.data_type ? (
                                    <div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 uppercase tracking-widest mb-1">
                                            <Database className="h-3 w-3" /> Data Type
                                        </div>
                                        <div className="text-[12px] text-[#00FFFF] uppercase">{incident.data_type}</div>
                                    </div>
                                ) : null}
                                {incident.log_date ? (
                                    <div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 uppercase tracking-widest mb-1">
                                            <Calendar className="h-3 w-3" /> Log Date
                                        </div>
                                        <div className="text-[12px] text-slate-300">
                                            {new Date(incident.log_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {/* Editable Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Severity */}
                            <div className="space-y-2">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    Severity Level *
                                </label>
                                <Select
                                    value={formData.severity}
                                    onValueChange={(value) => handleInputChange('severity')(value as SeverityType)}
                                >
                                    <SelectTrigger className={`bg-[#0A0B0E] border-[#1A1D24] text-slate-300 h-10 text-[12px] focus:ring-0 focus:border-[#50A970] ${validationErrors.severity ? 'border-[#FF3333]' : ''}`}>
                                        <SelectValue placeholder="Select severity" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0B0E] border-[#1A1D24] text-slate-300">
                                        {SEVERITY_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value} className="focus:bg-[#161A23] focus:text-white cursor-pointer text-[12px]">
                                                <div className="flex items-center gap-2 uppercase tracking-wider">
                                                    <div className={`w-2 h-2 rounded-full bg-${option.color}-500`} />
                                                    {option.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {validationErrors.severity ? (
                                    <p className="text-[#FF3333] text-[10px] tracking-wide mt-1">{validationErrors.severity}</p>
                                ) : null}
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    System Status *
                                </label>
                                <Select
                                    value={formData.status}
                                    onValueChange={(value) => handleInputChange('status')(value as StatusType)}
                                >
                                    <SelectTrigger className={`bg-[#0A0B0E] border-[#1A1D24] text-slate-300 h-10 text-[12px] focus:ring-0 focus:border-[#50A970] ${validationErrors.status ? 'border-[#FF3333]' : ''}`}>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0B0E] border-[#1A1D24] text-slate-300">
                                        {STATUS_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value} className="focus:bg-[#161A23] focus:text-white cursor-pointer text-[12px]">
                                                <div className="flex items-center gap-2 uppercase tracking-wider">
                                                    <div className={`w-2 h-2 rounded-full bg-${option.color}-500`} />
                                                    {option.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {validationErrors.status ? (
                                    <p className="text-[#FF3333] text-[10px] tracking-wide mt-1">{validationErrors.status}</p>
                                ) : null}
                            </div>
                        </div>

                        {/* Source */}
                        <div className="space-y-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Data Source / Engine
                            </label>
                            <Input
                                type="text"
                                value={formData.source || ''}
                                onChange={(e) => handleInputChange('source')(e.target.value)}
                                placeholder="Enter source intelligence provider..."
                                error={validationErrors.source}
                                className="bg-[#0A0B0E] border-[#1A1D24] text-slate-300 focus:border-[#50A970] h-10 text-[12px]"
                            />
                        </div>

                        {/* Tags */}
                        <div className="space-y-3 pt-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Custom Tag Arrays
                            </label>

                            {/* Current Tags */}
                            {formData.tags && Object.keys(formData.tags).length > 0 ? (
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {Object.entries(formData.tags).map(([key, value]) => (
                                        <span
                                            key={key}
                                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#00FFFF]/10 text-[#00FFFF] border border-[#00FFFF]/30 rounded text-[10px] tracking-widest uppercase"
                                        >
                                            <Tag className="h-3 w-3" />
                                            {String(value)}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(key)}
                                                className="ml-1 text-[#00FFFF]/70 hover:text-[#FF3333] transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            ) : null}

                            {/* Add New Tag */}
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <Input
                                        type="text"
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        placeholder="Define new tag identifier..."
                                        onKeyDown={handleKeyPress}
                                        className="bg-[#0A0B0E] border-[#1A1D24] text-slate-300 focus:border-[#50A970] h-10 text-[12px] w-full"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddTag}
                                    disabled={!newTag.trim()}
                                    className="px-5 font-bold uppercase tracking-widest text-[10px] bg-[#12151C] border border-[#1A1D24] text-slate-300 rounded hover:text-[#00FFFF] hover:border-[#00FFFF]/50 transition-all disabled:opacity-50"
                                >
                                    Add Tag
                                </button>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2 pt-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Analytical Notes
                            </label>
                            <Textarea
                                value={formData.notes || ''}
                                onChange={(e) => handleInputChange('notes')(e.target.value)}
                                placeholder="Append analyst remarks or internal investigation notes..."
                                rows={4}
                                className="bg-[#0A0B0E] border-[#1A1D24] text-slate-300 focus:border-[#50A970] text-[12px] custom-scrollbar p-3 w-full"
                            />
                        </div>

                        {/* Error Display */}
                        {error ? (
                            <div className="flex items-center gap-2 p-3 bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-lg animate-fade-in">
                                <AlertCircle className="h-4 w-4 text-[#FF3333]" />
                                <span className="text-[#FF3333] text-[11px] tracking-wide">
                                    [ERROR] Failed to update network incident. Please check your connection and try again.
                                </span>
                            </div>
                        ) : null}
                    </form>
                </div>

                {/* Actions Footer */}
                <div className="flex justify-end gap-3 p-5 bg-[#12151C] border-t border-[#1A1D24] flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                    >
                        Abort
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest bg-[#50A970] text-black rounded hover:bg-[#3d8556] transition-colors disabled:opacity-50 shadow-[0_0_15px_rgba(80,169,112,0.15)]"
                    >
                        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        {isLoading ? 'Committing...' : 'Commit Changes'}
                    </button>
                </div>
            </div>

            {/* CSS override for inner inputs inside generic UI library components */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .cyber-form-override input, .cyber-form-override textarea {
                    background-color: #0A0B0E !important;
                    border-color: #1A1D24 !important;
                    color: #cbd5e1 !important;
                    font-family: monospace !important;
                }
                .cyber-form-override input:focus, .cyber-form-override textarea:focus {
                    border-color: #50A970 !important;
                    box-shadow: none !important;
                    outline: none !important;
                }
                .cyber-form-override input::placeholder, .cyber-form-override textarea::placeholder {
                    color: #475569 !important;
                }
            `}} />
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default EditIncidentModal;